<template>
    <div class="mb-8" :id="`addressItem${this.subscription.id}`">
        <box-accordion
            v-if="!showEditForm && !showPauseForm"
            :force-expand-state="shouldExpand"
            @toggleExpand="toggleExpand">
            <template slot="summary">
                <p class="m-0 p-4 text-left" v-html="getFormattedAddress"></p>
            </template>
            <div>
                <!-- Quantity -->
                <address-manage-quantity
                    class="border-t border-black"
                    v-if="allowEdit"
                    :label="$t('MANAGE_DELIVERY_BOXES_PER')"
                    :subscription="subscription"/>

                <delivery-price headingKey="MANAGE_DELIVERY_COST_EACH"  :price="subscriptionsPrice"/>

                <!-- Edit address -->
                <div class="deliveryAddressCard__action border-t" v-if="allowEdit" @click="toggleEditForm">
                    <p class="no-margin" v-text="$t('ADDRESS_ITEM_EDIT')"></p>
                    <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/arrow-right-black.svg">
                </div>

                <pause-button
                    v-if="isDEU"
                    :subscription="subscription"
                    @paused="togglePauseForm"/>

                <!-- Cancel -->
                <div class="deliveryAddressCard__action" v-if="allowCancel && !getPauseIsCancel" @click="toggleCancelModal">
                    <p class="no-margin" v-text="$t('ADDRESS_ITEM_STOP')"></p>
                    <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/nav-close.svg">
                </div>

                <!-- Legal Cancellation for US-->
                <div v-if="getPauseIsCancel && !isPaused" class="deliveryAddressCard__action border-t" @click="pauseSubscription">
                    <p class="no-margin" v-text="$t('MANAGE_DELIVERY_CANCEL')"></p>
                    <img class="deliveryAddressCard__actionIcon" src="@/assets/images/decoration/nav-close.svg">
                </div>

                <resume-deliveries
                    v-if="getPauseIsCancel && isPaused"
                    :subscription="subscription"/>

                <!-- Restart -->
                <restart-deliveries-action
                    v-if="allowRestart && !getPauseIsCancel"
                    @restarted="$emit('closeList')"
                    :subscription="subscription"
                    :address="address"/>
            </div>
        </box-accordion>

        <edit-delivery-address-form
            v-if="showEditForm"
            :subscription-id="subscriptionId"
            :address="address"
            :delivery-comment="subscription.delivery_comment"
            :property-type="address.property_type"
            @closed="toggleEditForm"/>

        <pause-deliveries
            v-if="showPauseForm"
            :subscriptionId="subscription.id"
            :addressId="addressId"
            :isSubscriptionPaused="false"
            @deliveries-paused="handleDeliveriesPaused"
            @closed="togglePauseForm"/>

        <pause-deliveries-confirmation-modal v-if="showPauseConfirmationModal"/>

        <cancel-subscription-modal
            v-if="showCancelModal && !getPauseIsCancel"
            :subscription-id="subscriptionId"
            @closed="toggleCancelModal"/>

        <cancel-subscription-US
            v-if="showCancelModal && getPauseIsCancel"
            :subscription-id="subscription.id"
            @closed="toggleCancelModal"
            :nextDeliveryDate="displayDate"
            :isCutOffTimePassed="isCutOffTimePassed"
            :error="error"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { SUBSCRIPTION_STATUS_PAUSED, SUBSCRIPTION_STATUS_CANCELLED } from '@/constants/Subscription';
import BoxAccordion from '@/components/structure/BoxAccordion';
import CancelSubscriptionModal from '@/components/modals/CancelSubscriptionModal';
import PauseDeliveriesConfirmationModal from '@/components/modals/PauseDeliveriesConfirmationModal';
import CancelSubscriptionUS from '@/components/modals/CancelSubscriptionUS';
import EditDeliveryAddressForm from '@/components/forms/addresses/EditDeliveryAddressForm';
import DeliveryPrice from '@/components/partials/account/my-delivery-addresses/DeliveryPrice';
import AddressManageQuantity from '@/components/partials/account/my-delivery-addresses/AddressManageQuantity';
import RestartDeliveriesAction from '@/components/partials/account/my-delivery-addresses/RestartDeliveriesAction';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import { formatDisplayDate } from '@/helpers/DateHelpers';
import PauseButton from './PauseButton.vue';
import PauseDeliveries from '../my-delivery-schedule/manage-delivery/PauseDeliveries';
import ResumeDeliveries from '@/components/partials/account/my-delivery-schedule/manage-delivery/ResumeDeliveries';
import { getFormattedAddressForCountry } from '@/helpers/AddressHelpers';

let subscriptionService = new SubscriptionService();

export default {
    name: 'AddressItem',
    data() {
        return {
            showCancelModal: false,
            showEditForm: false,
            showPauseForm: false,
            showPauseConfirmationModal: false,
            error: null,
        };
    },
    props: {
        addressId: {
            type: Number,
            required: true,
        },
        allowEdit: {
            default: false,
            type: Boolean,
        },
        allowCancel: {
            type: Boolean,
            default: false,
        },
        allowRestart: {
            type: Boolean,
            default: false,
        },
        allowPause: {
            type: Boolean,
            default: true,
        },
        shouldExpand: {
            required: true,
        },
        subscriptionId: {
            type: Number,
            required: true,
        },
    },
    mounted() {
        this.$store.dispatch('address/fetchAllAddresses');
    },
    methods: {
        toggleCancelModal() {
            this.showCancelModal = !this.showCancelModal;
        },

        toggleEditForm() {
            this.showPauseForm = false;
            this.showEditForm = !this.showEditForm;
        },

        togglePauseForm() {
            this.showEditForm = false;
            this.showPauseForm = !this.showPauseForm;
        },
        handleDeliveriesPaused() {
            this.showPauseConfirmationModal = true;
        },

        toggleExpand() {
            this.$emit('expandSubscription', this.isExpanded === false ? this.subscriptionId : 0);
        },
        pauseSubscription() {
            subscriptionService.pauseSubscription({ subscriptionId: this.subscription.id, reason: 'default' })
                .then(() => {
                    this.showCancelModal = true;
                    this.toggleExpand();
                    this.fetchData();
                })
                .catch(error => {
                    this.error = error.response.data.errors.coupon[0] || this.$t('LEGAL_CANCEL_US_CASE_3');
                    this.toggleCancelModal();
                });
        },
        fetchData() {
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('scheduledOrder/fetchAllScheduledOrders', true);
        },
    },
    computed: {
        ...mapGetters({
            deliveries: 'scheduledOrder/getDeliveries',
            getPauseIsCancel: 'subscription/getPauseIsCancel',
            fullName: 'account/getFullName',
            isDEU: 'localisation/isDEU',
        }),
        address() {
            return this.$store.getters['address/getAddressById'](this.addressId);
        },
        allowDelete() {
            return this.subscription.status === SUBSCRIPTION_STATUS_CANCELLED;
        },
        subscription() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        isExpanded() {
            return this.shouldExpand;
        },
        subscriptionsPrice() {
            if (this.subscription.shop_total) {
                const subscriptionTotal = this.subscription.price - this.subscription.shop_total;
                return subscriptionTotal;
            }
            return this.subscription.price;
        },
        hideForCountry() {
            return !['USA', 'NLD'].includes(process.env.VUE_APP_COUNTRY_ISO);
        },
        getFormattedAddress() {
            return getFormattedAddressForCountry(this.address, this.fullName);
        },

        isPaused() {
            return this.subscription.status === SUBSCRIPTION_STATUS_PAUSED;
        },

        isCutOffTimePassed() {
            const upcomingDeliveryDate = this.deliveries.filter(item => item.datetime === this.nextDeliveryDate);
            return upcomingDeliveryDate?.cut_off_expired;
        },

        nextDeliveryDate() {
            return this.subscription?.next_delivery_date;
        },

        displayDate() {
            return this.nextDeliveryDate && formatDisplayDate(this.nextDeliveryDate);
        },
    },
    watch: {
        isExpanded(value) {
            if (value === false) this.showEditForm = false;
        },
    },
    components: {
        DeliveryPrice,
        CancelSubscriptionModal,
        CancelSubscriptionUS,
        EditDeliveryAddressForm,
        AddressManageQuantity,
        RestartDeliveriesAction,
        BoxAccordion,
        PauseButton,
        PauseDeliveries,
        PauseDeliveriesConfirmationModal,
        ResumeDeliveries,
    },
};
</script>
